import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, Loader2 } from 'lucide-react';

/** Brand-coloured dot marker for intermediate stops. */
const stopIcon = L.divIcon({
  className: 'fluo-stop-icon',
  html: `<span style="display:inline-block;width:14px;height:14px;border-radius:50%;background:#191998;border:3px solid #FFCD00;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></span>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const endpointIcon = L.divIcon({
  className: 'fluo-stop-icon endpoint',
  html: `<span style="display:inline-block;width:18px;height:18px;border-radius:50%;background:#E2001A;border:3px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.4)"></span>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

/** Pulsing train marker — drawn as inline SVG so it stays crisp on retina. */
const trainIcon = (color) => L.divIcon({
  className: 'fluo-train-icon',
  html: `
    <span style="position:relative;display:inline-block;width:34px;height:34px;">
      <span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:0.25;animation:fluoPulse 1.8s ease-out infinite"></span>
      <span style="position:absolute;inset:4px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2a7 7 0 0 0-7 7v6a3 3 0 0 0 3 3l-1.5 2h2.2l1-1.5h4.6l1 1.5h2.2L16 18a3 3 0 0 0 3-3V9a7 7 0 0 0-7-7zm-4 8a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2zm.5 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm7 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
        </svg>
      </span>
    </span>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const userIcon = L.divIcon({
  className: 'fluo-user-icon',
  html: `
    <span style="position:relative;display:inline-block;width:22px;height:22px;">
      <span style="position:absolute;inset:0;border-radius:50%;background:#2563EB;opacity:0.25;animation:fluoPulse 2s ease-out infinite"></span>
      <span style="position:absolute;inset:4px;border-radius:50%;background:#2563EB;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></span>
    </span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

/** Parse an "HH:MM" string into a Date for today (UTC-agnostic, local TZ). */
const parseHHMM = (hhmm) => {
  if (!hhmm || hhmm === '—') return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return null;
  const d = new Date();
  d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  return d;
};

/** Linear interpolation between two [lat, lon] points. */
const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/**
 * Returns the train's estimated [lat, lon] at `now` given the chronological
 * list of stops, OR `null` if the train hasn't departed / has already arrived.
 *
 * Logic:
 *  - Apply the live `delayMin` shift uniformly to every stop time.
 *  - Find the segment [i, i+1] where now ∈ [departure_i, arrival_{i+1}].
 *  - Interpolate linearly between the two stop coordinates.
 *  - If `now` is between an arrival and the next departure (dwell time at a
 *    stop), pin the marker to that stop.
 */
const estimateTrainPosition = (points, now, delayMin) => {
  const offsetMs = (delayMin || 0) * 60 * 1000;
  const shifted = points.map(p => {
    const dep = parseHHMM(p.departure || p.arrival);
    const arr = parseHHMM(p.arrival || p.departure);
    return {
      latlng: p.latlng,
      dep: dep ? new Date(dep.getTime() + offsetMs) : null,
      arr: arr ? new Date(arr.getTime() + offsetMs) : null,
    };
  });

  const first = shifted[0];
  const last = shifted[shifted.length - 1];
  if (!first?.dep || !last?.arr) return null;
  if (now < first.dep) return null;             // train not yet departed
  if (now > last.arr) return null;              // train already at terminus

  for (let i = 0; i < shifted.length - 1; i++) {
    const a = shifted[i];
    const b = shifted[i + 1];
    if (!a?.dep || !b?.arr) continue;
    // Dwell at stop a
    if (a.arr && now >= a.arr && now < a.dep) return a.latlng;
    // Travelling between a and b
    if (now >= a.dep && now <= b.arr) {
      const total = b.arr.getTime() - a.dep.getTime();
      if (total <= 0) return b.latlng;
      const t = (now.getTime() - a.dep.getTime()) / total;
      return lerp(a.latlng, b.latlng, Math.max(0, Math.min(1, t)));
    }
  }
  return null;
};

/** Map status → polyline colour (matches the status pills used elsewhere). */
const STATUS_COLOR = {
  on_time: '#047857',   // emerald-700
  delayed: '#B45309',   // amber-700
  cancelled: '#B91C1C', // red-700
};

/** Internal helper component — exposes the map instance + handles fitting +
 *  re-centering when the user clicks "Locate me". */
const MapController = ({ bounds, focusOn, onMap }) => {
  const map = useMap();
  useEffect(() => { onMap(map); }, [map, onMap]);
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, bounds]);
  useEffect(() => {
    if (focusOn) map.flyTo(focusOn, Math.max(map.getZoom(), 12), { duration: 0.8 });
  }, [map, focusOn]);
  return null;
};

const TrainMap = ({ stops, status = 'on_time', delayMin = 0 }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(null);
  const [focusOn, setFocusOn] = useState(null);
  const mapRef = useRef(null);

  // Refresh the estimated position every 30 s so the train pin moves on its own.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const points = useMemo(
    () => (stops || [])
      .filter(s => typeof s.lat === 'number' && typeof s.lon === 'number')
      .map(s => ({ ...s, latlng: [s.lat, s.lon] })),
    [stops]
  );

  const bounds = useMemo(
    () => (points.length === 0 ? null : L.latLngBounds(points.map(p => p.latlng))),
    [points]
  );

  const trainPos = useMemo(() => {
    if (status === 'cancelled' || points.length < 2) return null;
    return estimateTrainPosition(points, now, delayMin);
  }, [points, now, delayMin, status]);

  const lineColor = STATUS_COLOR[status] || STATUS_COLOR.on_time;
  const isCancelled = status === 'cancelled';

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) { setLocateError(t('train.map_geoloc_unsupported')); return; }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(ll);
        setFocusOn(ll);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocateError(err.code === 1 ? t('train.map_geoloc_denied') : t('train.map_geoloc_failed'));
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  }, [t]);

  if (points.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-slate-500" data-testid="train-map-empty">
        {t('train.map_no_coords')}
      </div>
    );
  }

  const center = bounds.getCenter();

  return (
    <div data-testid="train-map-block">
      <style>{`
        @keyframes fluoPulse {
          0%   { transform: scale(0.7); opacity: 0.6; }
          70%  { transform: scale(1.4); opacity: 0; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        .fluo-map-control {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 6px 10px;
          font-size: 12px; font-weight: 600; color: #191998;
          display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
          transition: background 0.15s ease;
        }
        .fluo-map-control:hover { background: #F1F5F9; }
        .fluo-map-control:disabled { opacity: 0.6; cursor: progress; }
      `}</style>

      <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={9}
          scrollWheelZoom={false}
          style={{ height: 460, width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />

          <Polyline
            positions={points.map(p => p.latlng)}
            pathOptions={{
              color: lineColor,
              weight: 4,
              opacity: isCancelled ? 0.5 : 0.9,
              dashArray: isCancelled ? '4 6' : '8 6',
            }}
          />

          {points.map((p, idx) => {
            const isEndpoint = idx === 0 || idx === points.length - 1;
            const time = p.departure || p.arrival || '—';
            return (
              <Marker
                key={`${p.name}-${idx}`}
                position={p.latlng}
                icon={isEndpoint ? endpointIcon : stopIcon}
                title={p.name}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold text-ter-blue mb-0.5">{p.name}</div>
                    <div className="font-mono tabular-nums text-slate-700">{time}</div>
                    {p.platform && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {t('board.platform_label')} <strong className="text-ter-blue">{p.platform}</strong>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {trainPos && (
            <Marker position={trainPos} icon={trainIcon(lineColor)} title={t('train.map_train_position')}>
              <Popup>
                <div className="text-xs">
                  <div className="font-bold text-ter-blue mb-0.5">{t('train.map_train_position')}</div>
                  <div className="text-[11px] text-slate-500">{t('train.map_position_disclaimer')}</div>
                  {delayMin > 0 && (
                    <div className="mt-1 text-[11px] text-amber-700 font-semibold">+{delayMin} min</div>
                  )}
                </div>
              </Popup>
            </Marker>
          )}

          {userPos && (
            <Marker position={userPos} icon={userIcon} title={t('train.map_you_are_here')}>
              <Popup><span className="text-xs font-semibold text-ter-blue">{t('train.map_you_are_here')}</span></Popup>
            </Marker>
          )}

          <MapController bounds={bounds} focusOn={focusOn} onMap={(m) => { mapRef.current = m; }} />
        </MapContainer>

        {/* Locate-me floating control */}
        <div className="absolute top-3 right-3 z-[400] flex flex-col gap-2">
          <button
            type="button"
            onClick={handleLocate}
            disabled={locating}
            className="fluo-map-control"
            data-testid="train-map-locate-btn"
            aria-label={t('train.map_locate_me')}
          >
            {locating
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Crosshair className="w-3.5 h-3.5" />}
            <span>{t('train.map_locate_me')}</span>
          </button>
          {locateError && (
            <div className="bg-red-50 text-red-800 border border-red-200 rounded-md px-2.5 py-1.5 text-[11px] font-semibold max-w-[260px] shadow-sm">
              {locateError}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-[11px] text-slate-500">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-ter-red" />
            {t('train.map_legend_endpoints')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-ter-blue ring-2 ring-ter-yellow" />
            {t('train.map_legend_stops', { count: Math.max(0, points.length - 2) })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-1 rounded" style={{ background: lineColor }} />
            {t(`train.map_legend_status_${status}`)}
          </span>
          {trainPos && (
            <span className="inline-flex items-center gap-1.5 font-semibold text-ter-blue">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: lineColor, border: '2px solid #fff', boxShadow: '0 0 0 1px ' + lineColor }} />
              {t('train.map_train_live')}
            </span>
          )}
        </div>
        <div className="text-right">{t('train.map_attribution')}</div>
      </div>
    </div>
  );
};

export default TrainMap;
